import { useEffect, useState } from "react";
import { api, getToken } from "@/lib/api";
import { apiErrorMessage } from "@/AuthContext";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

function authHeaders() {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function EmailPreviewStudio({ selectedId, onSelect }) {
  const [items, setItems] = useState([]);
  const [html, setHtml] = useState("");
  const [subject, setSubject] = useState("");
  const [error, setError] = useState("");
  const [listLoading, setListLoading] = useState(true);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [toEmail, setToEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sendNote, setSendNote] = useState("");
  const [mode, setMode] = useState("sample");
  const [userEmail, setUserEmail] = useState("");
  const [hits, setHits] = useState([]);
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [broadcastBusy, setBroadcastBusy] = useState(false);
  const [eligibleInfo, setEligibleInfo] = useState(null);
  const [broadcast, setBroadcast] = useState(null);

  const isWeekly = selectedId === "weekly_summary";
  const useReal = isWeekly && mode === "real";

  useEffect(() => {
    let cancelled = false;
    setListLoading(true);
    api
      .get("/admin/email-previews", { headers: authHeaders() })
      .then((res) => {
        if (!cancelled) setItems(Array.isArray(res.data) ? res.data : []);
      })
      .catch((err) => {
        if (!cancelled) setError(apiErrorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setListLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setMode("sample");
    setUserEmail("");
    setHits([]);
  }, [selectedId]);

  useEffect(() => {
    if (!selectedId) {
      setHtml("");
      setSubject("");
      setSendNote("");
      return;
    }
    if (useReal) {
      return;
    }
    let cancelled = false;
    setPreviewLoading(true);
    setError("");
    setSendNote("");
    api
      .get(`/admin/email-previews/${encodeURIComponent(selectedId)}`, {
        headers: authHeaders(),
      })
      .then((res) => {
        if (cancelled) return;
        setSubject(res.data?.subject || "");
        setHtml(typeof res.data?.html === "string" ? res.data.html : "");
      })
      .catch((err) => {
        if (!cancelled) {
          setHtml("");
          setSubject("");
          setError(apiErrorMessage(err));
        }
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedId, useReal]);

  useEffect(() => {
    if (!useReal) return undefined;
    const needle = userEmail.trim();
    if (needle.length < 2) {
      setHits([]);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      api
        .get("/admin/email-preview-users", {
          headers: authHeaders(),
          params: { q: needle },
        })
        .then((res) => {
          if (!cancelled) setHits(Array.isArray(res.data) ? res.data : []);
        })
        .catch(() => {
          if (!cancelled) setHits([]);
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [useReal, userEmail]);

  useEffect(() => {
    if (!useReal) return undefined;
    const needle = userEmail.trim();
    if (!needle || !needle.includes("@")) {
      setHtml("");
      setSubject("Your MinutePay weekly report");
      return undefined;
    }
    let cancelled = false;
    setPreviewLoading(true);
    setError("");
    api
      .get("/admin/email-previews/weekly_summary/real", {
        headers: authHeaders(),
        params: { user_email: needle },
      })
      .then((res) => {
        if (cancelled) return;
        setSubject(res.data?.subject || "");
        setHtml(typeof res.data?.html === "string" ? res.data.html : "");
      })
      .catch((err) => {
        if (!cancelled) {
          setHtml("");
          setError(apiErrorMessage(err));
        }
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [useReal, userEmail]);

  async function sendTest(e) {
    e.preventDefault();
    if (!selectedId || sending) return;
    setSending(true);
    setError("");
    setSendNote("");
    try {
      const res = useReal
        ? await api.post(
          "/admin/email-previews/weekly_summary/send-test-real",
          { user_email: userEmail.trim(), to_email: toEmail.trim() },
          { headers: authHeaders() }
        )
        : await api.post(
          `/admin/email-previews/${encodeURIComponent(selectedId)}/send-test`,
          { to_email: toEmail.trim() },
          { headers: authHeaders() }
        );
      const id = res.data?.message_id || "";
      const provider = res.data?.provider || "email";
      setSendNote(id ? `Sent via ${provider}. Message ID: ${id}` : `Sent via ${provider}.`);
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setSending(false);
    }
  }

  const canSend = toEmail.trim() && (!useReal || userEmail.trim());
  const selectedItem = items.find((item) => item.id === selectedId);
  const selectedLabel = selectedItem?.label || selectedItem?.subject || selectedId;

  async function openBroadcast() {
    if (!selectedId || broadcastBusy) return;
    setError("");
    setBroadcastBusy(true);
    try {
      const res = await api.get("/admin/email-broadcasts/eligible", {
        headers: authHeaders(),
        params: { email_type: selectedId },
      });
      const info = res.data || null;
      setEligibleInfo(info);
      if (info?.broadcast_ready === false) {
        setError(info.broadcast_blocked_reason || "Broadcast is not available yet.");
        return;
      }
      setBroadcastOpen(true);
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setBroadcastBusy(false);
    }
  }

  async function confirmBroadcast() {
    if (!selectedId) return;
    setBroadcastBusy(true);
    setError("");
    try {
      const res = await api.post(
        "/admin/email-broadcasts",
        { email_type: selectedId, confirm: true },
        { headers: authHeaders() }
      );
      setBroadcast(res.data || null);
      setBroadcastOpen(false);
      const id = res.data?.broadcast_id;
      if (!id) return;
      for (let i = 0; i < 120; i += 1) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        const status = await api.get(`/admin/email-broadcasts/${encodeURIComponent(id)}`, {
          headers: authHeaders(),
        });
        setBroadcast(status.data || null);
        if (status.data?.status && status.data.status !== "running") break;
      }
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setBroadcastBusy(false);
    }
  }

  return (
    <div className="mp-preview-studio">
      {error ? <p className="mp-error">{error}</p> : null}
      {listLoading ? <p className="mpd-muted">Loading templates…</p> : null}

      {!listLoading ? (
        <div className="mp-preview-grid">
          {items.map((item) => (
            <article
              key={item.id}
              className={`mp-status ${selectedId === item.id ? "is-on mp-preview-selected" : ""}`}
              aria-current={selectedId === item.id ? "true" : undefined}
            >
              <p className="mp-kicker">{item.label && item.label !== item.subject ? item.label : item.id}</p>
              <h2 style={{ margin: "0 0 12px", fontSize: 18 }}>{item.subject}</h2>
              {selectedId === item.id ? <p className="mpd-muted">Currently selected</p> : null}
              <Button
                type="button"
                variant={selectedId === item.id ? "default" : "outline"}
                onClick={() => onSelect(item.id)}
              >
                {selectedId === item.id ? "Selected" : "Select"}
              </Button>
            </article>
          ))}
        </div>
      ) : null}

      {previewLoading ? <p className="mpd-muted">Loading backend preview…</p> : null}

      {selectedId && !previewLoading ? (
        <div className="mp-preview-result">
          <p className="mp-kicker">{selectedId}</p>
          <h2 style={{ margin: "0 0 8px", fontSize: 22 }}>{subject}</h2>
          <p className="mp-lead">Selected template: {selectedLabel}</p>
          {isWeekly ? (
            <div className="mp-preview-modes" role="radiogroup" aria-label="Test data source">
              <label>
                <input
                  type="radio"
                  name="preview-mode"
                  checked={mode === "sample"}
                  onChange={() => setMode("sample")}
                />
                Sample Data
              </label>
              <label>
                <input
                  type="radio"
                  name="preview-mode"
                  checked={mode === "real"}
                  onChange={() => setMode("real")}
                />
                Real User Data
              </label>
            </div>
          ) : null}
          <form className="mp-preview-send" onSubmit={sendTest}>
            {useReal ? (
              <label>
                MinutePay user email
                <input
                  type="email"
                  required
                  list="mp-preview-users"
                  value={userEmail}
                  onChange={(e) => setUserEmail(e.target.value)}
                  placeholder="existing-user@example.com"
                  autoComplete="off"
                />
                <datalist id="mp-preview-users">
                  {hits.map((hit) => (
                    <option key={hit.email} value={hit.email}>
                      {hit.full_name ? `${hit.full_name} (${hit.email})` : hit.email}
                    </option>
                  ))}
                </datalist>
              </label>
            ) : null}
            <label>
              Send test to
              <input
                type="email"
                required
                value={toEmail}
                onChange={(e) => setToEmail(e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
              />
            </label>
            {useReal ? (
              <Button
                type="button"
                variant="outline"
                disabled={!userEmail.trim()}
                onClick={() => setToEmail(userEmail.trim())}
              >
                Send to selected user’s email
              </Button>
            ) : null}
            <Button type="submit" disabled={sending || !canSend}>
              {sending ? "Sending…" : "Send Test"}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="mp-broadcast-btn"
              disabled={broadcastBusy || !selectedId}
              onClick={openBroadcast}
            >
              {broadcastBusy ? "Working…" : "Send to All Users"}
            </Button>
          </form>
          {sendNote ? <p className="mpd-muted">{sendNote}</p> : null}
          {broadcast ? (
            <p className="mp-lead">
              Broadcast {broadcast.status}: sent {broadcast.sent} · skipped {broadcast.skipped} · failed{" "}
              {broadcast.failed} · eligible {broadcast.eligible}
            </p>
          ) : null}
          {html ? (
            <iframe
              className="mp-preview-frame"
              title={subject || selectedId}
              sandbox=""
              srcDoc={html}
            />
          ) : null}
        </div>
      ) : null}

      <AlertDialog open={broadcastOpen} onOpenChange={setBroadcastOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Send to all eligible users?</AlertDialogTitle>
            <AlertDialogDescription>
              This sends <strong>{eligibleInfo?.subject || selectedLabel}</strong> ({selectedId}) to{" "}
              <strong>{eligibleInfo?.eligible ?? 0}</strong> eligible MinutePay users. Each recipient
              gets their own name and email. Admins, invalid emails, and opted-out users are excluded.
              This is a one-time campaign, not a lifecycle cron.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={broadcastBusy}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmBroadcast} disabled={broadcastBusy}>
              Send to {eligibleInfo?.eligible ?? 0} users
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
